<template>
  <base-toast :classes="toastClasses" :text="toastMessage"></base-toast>
  <h1 class="mt-4">{{ menu.attributes.name }} bestellen</h1>
  <p>
    Kontrollieren Sie ihre Bestellung und geben Sie alle notwendigen Daten ein.
    <base-required-text></base-required-text>
  </p>
  <div
    v-if="menu.errors?.pagecount"
    role="alert"
    class="alert mb-5 alert-primary border-primary shadow"
    v-html="menu.errors.pagecount[0]"
  ></div>
  <div class="row g-5 me-0">
    <div class="col-12 col-xl-4 order-md-last position-relative pe-0 mt-0 mt-xl-5">
      <div class="position-sticky mb-5 pb-0" style="top: 195px">
        <configuration-price :menu="menu" @update:data="changeData('menu', $event)"></configuration-price>
        <div class="d-flex justify-content-around mt-2">
          <a
            :href="'/menu-pdf/' + menu.id + '/preview'"
            target="_blank"
            class="btn btn-primary align-items-center w-100"
          >
            Druckvorschau Menükarte final
          </a>
        </div>

        <div class="d-flex justify-content-around mt-2">
          <a
            v-if="!downloadingPreview"
            href="#"
            class="btn btn-primary align-items-center w-100"
            @click.prevent="downloadPreview"
          >
            Download Web-Version
          </a>
          <a v-if="downloadingPreview" href="#" class="btn btn-primary align-items-center w-100">
            <img width="32" height="32" src="/storage/images/loading.gif" alt="Loading..." />
          </a>
        </div>
      </div>
    </div>

    <div class="col-12 col-xl-8 pe-0 pe-xl-4 mark-required">
      <base-box title="Rechnungsadresse">
        <div style="margin-bottom: -1rem">
          <base-address-form
            attribute="billingContact"
            :errors="errors?.billingContact"
            :address="order.billingContact"
            :show-uid="true"
            :supplemental-in-own-row="true"
            @update:data="changeData('order', $event)"
          ></base-address-form>
        </div>
      </base-box>
      <base-box title="Lieferadresse">
        <div class="checkbox" :class="!sameAddress && 'mb-3'">
          <label class="d-flex gap-2">
            <input v-model="sameAddress" name="sameAddress" type="checkbox" @change="removeError('deliveryContact')" />
            <div class="check"></div>
            Rechnungsadresse und Lieferadresse sind identisch.
          </label>
        </div>
        <div v-if="!sameAddress" style="margin-bottom: -1rem">
          <base-address-form
            attribute="deliveryContact"
            :errors="errors?.deliveryContact"
            :address="order.deliveryContact"
            :supplemental-in-own-row="true"
            :show-phone="true"
            @update:data="changeData('order', $event)"
          ></base-address-form>
        </div>
        <div v-else class="input-group mt-3">
          <span class="input-group-text fs-4 text-primary d-flex justify-content-center">
            <i class="bi bi-telephone"></i>
          </span>
          <base-form-input
            :errors="errors?.deliveryContact?.phone"
            :value="order?.deliveryContact?.phone"
            attribute="deliveryContact"
            label="Telefonnummer"
            name="phone"
            type="phone"
            :required="true"
            @update:data="changeData('order', $event)"
          ></base-form-input>
        </div>
      </base-box>

      <base-box v-if="menu == null || menu.attributes?.total != '0,00 €'" title="Zahlung">
        Nach Klick auf "Jetzt kostenpflichtig bestellen" werden sie auf die Seite unseres Bezahl-Dienstleisters
        weitergeleitet und können aus mehreren Zahlungsarten wählen.
      </base-box>
    </div>
  </div>
  <configuration-order-checkboxes
    :menu="menu"
    :disabled="formButtonDisabled || menu.errors || Object.keys(errors).length > 0"
    @submit="placeOrder"
  ></configuration-order-checkboxes>
</template>

<script>
import { onMounted, ref } from 'vue';

import { useVModel } from '@vueuse/core';

import useOrders from '@/composables/useOrders';
import useMenus from '@/composables/useMenus';
import useContacts from '@/composables/useContacts';

import merge from 'lodash/merge';
import forEach from 'lodash/forEach';

import ConfigurationPrice from '@/components/ConfigurationPrice.vue';
import ConfigurationOrderCheckboxes from '@/components/ConfigurationOrderCheckboxes.vue';

export default {
  components: {
    ConfigurationPrice,
    ConfigurationOrderCheckboxes,
  },
  props: {
    menu: {
      type: Object,
      default: null,
    },
    fetchMenu: {
      type: Function,
      default: null,
    },
  },
  emits: ['update:menu'],
  setup(props, { emit }) {
    const toastMessage = ref('');
    const toastClasses = ref('');
    const formButtonDisabled = ref(false);
    const errors = ref({});
    const menu = useVModel(props, 'menu', emit);
    const order = ref({});
    const downloadingPreview = ref(false);
    const sameAddress = ref(menu.value.attributes.data.sameAddress ?? true);

    const changeData = (target, param) => {
      const obj = { menu, order };
      let name = '';
      let attribute = '';
      let newObject = {};
      if (param.target) {
        attribute = param.target.dataset.attribute;
        name = param.target.getAttribute('name');
        const value = param.target.type == 'checkbox' ? param.target.checked : param.target.value;

        newObject = { [name]: value };
        if (attribute) {
          newObject = {
            [attribute]: newObject,
          };
        }
      } else {
        newObject = { data: { [param.name]: param.value } };
      }
      removeError(name, attribute);
      obj[target].value = merge({}, obj[target].value, newObject);
    };

    const removeError = (name, attribute) => {
      if (attribute) {
        if (errors.value[attribute]) {
          delete errors.value[attribute][name];
          if (
            (name == 'first_name' && !errors.value[attribute]['last_name']) ||
            (name == 'last_name' && !errors.value[attribute]['first_name'])
          ) {
            delete errors.value[attribute]['company'];
          }
          if (name == 'company') {
            delete errors.value[attribute]['first_name'];
            delete errors.value[attribute]['last_name'];
          }
          if (Object.keys(errors.value[attribute]).length == 0) {
            delete errors.value[attribute];
          }
        }
      } else {
        delete errors.value[name];
      }
    };

    const { saveOrder } = useOrders();
    const { fetchContactByStatus, contact } = useContacts();

    const placeOrder = async () => {
      formButtonDisabled.value = true;
      order.value.billingContact.status = 'BILLING';
      let orderParams = {
        menu_id: Number(menu.value.id),
        orderData: merge({}, order.value, {
          sameAddress: sameAddress.value,
        }),
      };
      const savedOrder = await saveOrder(orderParams);
      try {
        formButtonDisabled.value = false;
        const mollieURL = new URL(savedOrder);
        window.location.href = mollieURL;
      } catch (e) {
        forEach(savedOrder.errors, (v, k) => (errors.value[k] = v[0]));
        toastMessage.value = 'Fehler in Bestelldaten. Bitte prüfen!';
        toastClasses.value = 'bg-danger text-white ' + new Date().getTime();
      }
    };

    const { prepareExportMenu } = useMenus();

    const downloadPreview = async () => {
      downloadingPreview.value = true;

      try {
        const url = (await prepareExportMenu(menu.value.id))['url'];

        const link = document.createElement('a');
        link.href = '/downloadPreview/' + url;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (e) {
        console.error(e);
      }

      downloadingPreview.value = false;
    };


    onMounted(async () => {
      // get default CUSTOMER address
      await fetchContactByStatus('CUSTOMER');
      order.value.billingContact = contact.value?.attributes;
      order.value.deliveryContact = { phone: contact.value?.attributes.phone };

      menu.value = {
        data: {
          ['calculated']: Date.now(),
        },
      };
    });
    return {
      changeData,
      errors,
      formButtonDisabled,
      merge,
      order,
      placeOrder,
      removeError,
      sameAddress,
      toastClasses,
      toastMessage,
      downloadingPreview,
      downloadPreview,
    };
  },
};
</script>

<style lang="scss" scoped></style>
